import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteDocumentModal from "~/components/Document/deleteDocumentModal";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { ProjectApi } from "~/functions/api/project";
import { dateToString } from "~/functions/datetime";
import ContextMenu from "~/images/context_menu.svg?react";
import type { Document, UpdateDocumentData } from "~/interface/document";
import type { Project } from "~/interface/project";
import { setCurrentProject } from "~/slices/auth";
import type { RootState } from "~/store";
import { toDisplayString } from "~/utils/customDate";
import styles from "./index.module.scss";

const SummaryListView = () => {
  const [documents, setDocuments] = useState<Document[]>([]); // サンプルデータを直接セット
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState<string | null>(null);
  const [editedDocument, setEditedDocument] = useState<Partial<Document>>({});
  const [selectedId, setSelectedId] = useState<string | null>("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isGenerating] = useState<boolean>(false);
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);

  // 日時をHTML5 datetime-local形式に変換
  const formatDateForInput = (date: string | null | undefined): string => {
    if (!date) return "";
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // HTML5 datetime-localからISO文字列に変換
  const handleNativeDateChange = (value: string) => {
    if (value) {
      return new Date(value).toISOString();
    }
    return undefined;
  };

  const fetchProject = useCallback(async () => {
    if (!currentProject?.id) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return null;
    }

    const projectApi = new ProjectApi();
    const response = await projectApi.get(currentUser.organization_id, currentProject.id);
    if (response !== null) {
      const project = response;
      // 取得したプロジェクトIDが変わる場合のみ更新（無限リクエスト対策）
      if (project?.id !== currentProject.id) {
        dispatch(setCurrentProject(project));
      }
      return project;
    } else {
      toast.error(TOAST_MESSAGES.SUMMARY.FETCH_PROJECT_FAILED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return null;
    }
  }, [currentProject?.id, currentUser.organization_id, dispatch, navigate]);

  const fetchSummaryList = useCallback(
    async (project: Project) => {
      if (project !== null) {
        const documentApi = new DocumentApi();

        const response = await documentApi.getAllByType(
          currentUser.organization_id,
          project.id,
          "summary",
        );
        if (response !== null) {
          const documents = response;
          setDocuments(documents); // ステートにセット
          setIsLoading(false); // ローディング終了
        } else {
          setError(new Error("Failed to fetch summaries"));
          navigate(`/${currentUser.organization_id}/project/${project.id}/dashboard`);
        }
      }
    },
    [currentUser.organization_id, navigate],
  );

  // APIリクエストで議事録を取得
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const project = await fetchProject();
      if (!cancelled && project !== null) {
        await fetchSummaryList(project);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [fetchProject, fetchSummaryList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // dropdownMenu や contextMenuアイコンを含む領域外をクリックしたら閉じる処理
      const dropdownWrappers = document.querySelectorAll(`.${styles["dropdownWrapper"]}`);
      let isClickInside = false;

      dropdownWrappers.forEach((wrapper) => {
        if (wrapper.contains(event.target as Node)) {
          isClickInside = true;
        }
      });

      if (!isClickInside) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleUpdate = async (id: string) => {
    if (!editedDocument.name || editedDocument.name.trim() === "") {
      toast.error(TOAST_MESSAGES.SUMMARY.NAME_REQUIRED);
      return null;
    }

    const updatedDocument: UpdateDocumentData = {
      name: editedDocument.name,
      ...(editedDocument.meeting && {
        meeting: {
          ...(editedDocument.meeting.date && { date: editedDocument.meeting.date }),
          ...(editedDocument.meeting.url && { url: editedDocument.meeting.url }),
        },
      }),
    };

    const documentApi = new DocumentApi();

    const response = await documentApi.updateInfo(
      currentUser.organization_id,
      currentProject.id,
      id,
      updatedDocument,
    );
    if (response !== null) {
      setDocuments((prevDocuments) =>
        prevDocuments.map((document) => {
          if (document.id === id) {
            if (updatedDocument.name) {
              document.name = updatedDocument.name;
            }
            if (updatedDocument.meeting) {
              document.meeting = updatedDocument.meeting;
            }
            return document;
          } else {
            return document;
          }
        }),
      );
      setEditMode(null);
      setEditedDocument({});
    }
    return null;
  };

  const handleViewDetails = (document: Document) => {
    if (!editMode) {
      navigate(`${document.id}`, { state: { document } });
    }
  };

  // ローディング状態やエラーメッセージの表示
  if (isLoading) {
    return <div>ロード中...</div>;
  }

  if (error) {
    return <div>{error.message}</div>;
  }

  const handleEdit = async (document: Document) => {
    setEditMode(document.id);
    setEditedDocument({ ...document });
    setDropdownOpen(null);
  };

  const handleDeleteModalOpen = (id: string) => {
    setSelectedId(id);
    setIsDeleting(true);
  };

  const handleDeleteSuccess = (deletedId: string) => {
    setDocuments((prevDocuments) => prevDocuments.filter((document) => document.id !== deletedId));
    setIsDeleting(false); // モーダルを閉じる
    setSelectedId(null);
  };

  const handleCancel = async () => {
    setEditMode(null);
    setEditedDocument({});
  };

  const handleInputChange = (field: keyof Document, value: string) => {
    setEditedDocument((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div>
      {editMode && (
        <div className={styles["buttonGroup"]}>
          <Button label="保存" styleType="blue" onClick={() => handleUpdate(editMode)} />
          <Button label="キャンセル" styleType="white" onClick={() => handleCancel()} />
        </div>
      )}
      <table className={styles["meetingTable"]}>
        <thead>
          <tr>
            <th>名前</th>
            <th>打ち合わせ日時</th>
            <th>最終更新日</th>
            <th className={styles["meetingTable-detail-button"]}></th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr key={document.id} className={styles["clickableRow"]}>
              <td
                onClick={() => (editMode !== document.id ? handleViewDetails(document) : undefined)}
                style={{ cursor: editMode === document.id ? "default" : "pointer" }}
              >
                {editMode === document.id ? (
                  <input
                    className={styles["editableTitle"]}
                    type="text"
                    value={editedDocument.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  document.name
                )}
              </td>
              <td
                onClick={() => (editMode !== document.id ? handleViewDetails(document) : undefined)}
                style={{ cursor: editMode === document.id ? "default" : "pointer" }}
              >
                {editMode === document.id ? (
                  <input
                    type="datetime-local"
                    value={formatDateForInput(editedDocument.meeting?.date ?? null)}
                    onChange={(e) => {
                      setEditedDocument((prev) => {
                        const newDate = handleNativeDateChange(e.target.value);
                        return {
                          ...prev,
                          meeting: {
                            ...prev.meeting,
                            ...(newDate && { date: newDate }),
                            ...(prev.meeting?.url && { url: prev.meeting.url }),
                          },
                        };
                      });
                    }}
                    className={styles["dateTimeInput"]}
                  />
                ) : document.meeting?.date ? (
                  toDisplayString(document.meeting.date)
                ) : (
                  ""
                )}
              </td>
              <td onClick={() => handleViewDetails(document)} style={{ cursor: "pointer" }}>
                {dateToString(document.modified_at || document.created_at)}
              </td>
              <td>
                <div className={styles["dropdownWrapper"]}>
                  <ContextMenu
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // 常に開く
                      setDropdownOpen(document.id);
                    }}
                  />
                  {dropdownOpen === document.id && (
                    <div className={styles["dropdownMenu"]}>
                      <ul>
                        <li onClick={() => handleEdit(document)}>編集</li>
                        {!isGenerating && documents.length > 1 && (
                          <li
                            className={styles["deleteButton"]}
                            onClick={() => handleDeleteModalOpen(document.id)}
                          >
                            削除
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isDeleting && selectedId && (
        <DeleteDocumentModal
          onClose={() => setIsDeleting(false)}
          documentId={selectedId}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};

export default SummaryListView;
